import { Space, Switch, Spin, message, Button, Input, notification } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../../api/useAPIMutation';
import useAPIQuery from '../../../api/useAPIQuery';
import ErrorResult from '../../../components/ErrorResult';
import Form from '../../../components/Form';
import { withRouteChange } from '../../../services/APM';

interface StaffSankhyaConfigurationFormValues {
  enableSankhyaAdmission: boolean;
  sankhyaClientToken: string;
}

const StaffSankhyaConfiguration = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<StaffSankhyaConfigurationFormValues>();
  const enableSankhyaAdmissionValue = Form.useWatch('enableSankhyaAdmission', form);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const mutation = useAPIMutation(
    async (values: StaffSankhyaConfigurationFormValues, api) => {
      return await Promise.all([
        api.patch('/configs/tenant-configurations/enable-sankhya-admission/', {
          value: values.enableSankhyaAdmission,
        }),
        values.enableSankhyaAdmission &&
          api.patch('/candidate-register-fields/cpf/', {
            visible: 'YES',
            required: 'YES',
          }),
        api.patch('/configs/tenant-configurations/sankhya-client-token/', {
          value: values.sankhyaClientToken,
        }),
      ]);
    },
    {
      onSuccess: () => {
        message.success('Configurações atualizadas.');
      },
      onError: (err, variables, context) => {
        if (err.response?.data?.data === 'Invalid Sankhya Integration Token') {
          return notification.error({
            duration: 0,
            message: 'Erro',
            description:
              'Erro na configuração da integração com Sankhya: Token de Integração inválido.',
          });
        }

        if (
          err?.response?.data?.message ===
          'The SANKHYA_ADMISSION feature cannot be enabled while the VIXTING_ADMISSION feature is enabled.'
        ) {
          return notification.error({
            message: 'Erro',
            description:
              'A Integração com Admissão Digital da Sankhya não pode ser habilitado enquanto a Integração com Admissão Digital da Vixting está ativa.',
          });
        }
        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
    }
  );

  return (
    <ErrorResult error={config.error}>
      <Helmet>
        <title>Integração Sankhya | Área do CS | Recrutamento e Seleção</title>
      </Helmet>
      <Spin spinning={config.isLoading}>
        <Form<StaffSankhyaConfigurationFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            enableSankhyaAdmission: config.data?.enableSankhyaAdmission,
            sankhyaClientToken: config.data?.sankhyaClientToken,
          }}
          onFinish={(values) => {
            mutation.mutate(values);
          }}
        >
          <Space direction="vertical">
            <Form.Row align="middle">
              <Form.Col>
                <Form.Item
                  name="enableSankhyaAdmission"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
              </Form.Col>
              <Form.Col>Habilitar Integração com Admissão Digital da Sankhya</Form.Col>
            </Form.Row>
            <Form.Row align="middle">
              <Form.Col span={8}>
                <Form.Item
                  name="sankhyaClientToken"
                  label="Token de Integração"
                  rules={[{ required: enableSankhyaAdmissionValue }]}
                >
                  <Input disabled={!enableSankhyaAdmissionValue} />
                </Form.Item>
              </Form.Col>
            </Form.Row>
          </Space>
          <Form.Footer>
            <Form.FooterEnd>
              <Form.Item noStyle shouldUpdate={() => true}>
                {(form) => <Button onClick={() => form.resetFields()}>Cancelar</Button>}
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={mutation.isLoading}>
                Salvar
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(StaffSankhyaConfiguration);
